import React, { Component } from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (could also send to error tracking service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Here you could send to an error tracking service like Sentry
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#0a1628',
            p: 3,
          }}
        >
          <Card
            sx={{
              maxWidth: 500,
              width: '100%',
              background: '#132337',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Error Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'rgba(255, 107, 53, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <Typography sx={{ fontSize: 40 }}>⚠️</Typography>
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#ffffff',
                }}
              >
                Bir Şeyler Yanlış Gitti
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 3,
                  fontSize: '0.95rem',
                }}
              >
                Üzgünüz, beklenmeyen bir hata oluştu. Lütfen sayfayı yenileyin veya ana sayfaya dönün.
              </Typography>

              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    background: 'rgba(255, 0, 0, 0.1)',
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                    textAlign: 'left',
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#ff6b6b',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={this.handleRetry}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ff8555 0%, #ffa73e 100%)',
                    },
                  }}
                >
                  Yeniden Dene
                </Button>
                <Button
                  variant="outlined"
                  onClick={this.handleGoHome}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Ana Sayfa
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for using with hooks
export const withErrorBoundary = (WrappedComponent) => {
  return function WithErrorBoundaryWrapper(props) {
    return (
      <ErrorBoundary>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

// Error fallback component for inline use
export const ErrorFallback = ({ error, resetError }) => (
  <Box
    sx={{
      p: 3,
      textAlign: 'center',
      background: '#132337',
      borderRadius: 2,
    }}
  >
    <Typography sx={{ fontSize: 40, mb: 2 }}>😕</Typography>
    <Typography variant="h6" sx={{ mb: 1, color: '#ffffff' }}>
      Veri yüklenirken bir hata oluştu
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 2, fontSize: '0.9rem' }}>
      {error?.message || 'Bilinmeyen hata'}
    </Typography>
    {resetError && (
      <Button
        variant="contained"
        size="small"
        onClick={resetError}
        sx={{
          background: '#ff6b35',
          '&:hover': { background: '#ff8555' },
        }}
      >
        Tekrar Dene
      </Button>
    )}
  </Box>
);

export default ErrorBoundary;



